import React, {
 ChangeEvent,
 useEffect,
 useRef,
 useState,
} from 'react'
import {useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {Button, KTSVG, Model} from 'helpers'
import {TextField} from 'helpers/ui-components/form/TextField'
import {Select2DropdownField} from 'helpers/ui-components/form/Select2DropdownField'
import IDropdown from 'models/dropdown'
import {IFileObject} from 'models/Common'
import {IInfra} from 'models/MyAccount'
import {
 fetchDistrict,
 fetchState,
} from 'utils/api/auth/locationService'
import {fetchInfraTypes} from 'utils/api/auth/myAccountService'
import {generateRandomString} from 'utils/helper'
import {handleFileChange as uploadFileChange} from 'utils/helpers/fileUpload'

export type IInfraFormPayload = {
 id?: number
 infra_type: string
 state: number
 district: number
 pincode: string
 address: string
 is_active?: boolean
 gst_no?: string
 gst_certificate?: string
}

type IProps = {
 show: boolean
 editInfra?: IInfra | null
 onClose: () => void
 onSave: (values: IInfraFormPayload) => void
}

type IInfraTypeOption = {
 id: string | number
 name: string
 is_active?: boolean
}

const sharedSelectStyle = {
 transition: 'unset',
 boxShadow: 'unset',
 border: '0px',
 borderRadius: '0px',
 padding: '0px',
 minHeight: 'auto',
}

const sharedClassName =
 'font-normal !text-[#33393D] boxed-select2 relative block text-lg border-0 !rounded-l-lg'

const AddInfraModal = (props: IProps) => {
 const {t} = useTranslation()
 const [infraTypeList, setInfraTypeList] = useState<
  IInfraTypeOption[]
 >([])
 const [stateList, setStateList] = useState<IDropdown[]>([])
 const [districtList, setDistrictList] = useState<
  IDropdown[]
 >([])
 const [uploadedFileData, setUploadedFileData] =
  useState<IFileObject>({})
 const [uploading, setUploading] = useState(false)
 const fileInputId = `gst-cert-${generateRandomString(5)}`
 const prevStateRef = useRef<string | number>('')

 const {
  register,
  control,
  handleSubmit,
  reset,
  setValue,
  watch,
  formState: {errors, isValid},
 } = useForm({
  mode: 'all',
  reValidateMode: 'onChange',
  defaultValues: {
   infraType: '',
   state: '' as string | number,
   district: '' as string | number,
   pincode: '',
   address: '',
   gstNumber: '',
  },
 })

 const selectedState = watch('state')
 const isEditMode = !!props.editInfra?.id

 const ensureInfraTypeOption = (
  list: IInfraTypeOption[],
  id: string | number,
  name: string
 ): IInfraTypeOption[] => {
  if (list.some((item) => item.id === id)) {
   return list
  }
  return [{id, name, is_active: true}, ...list]
 }

 const ensureDropdownOption = (
  list: IDropdown[],
  id: number,
  name: string
 ): IDropdown[] => {
  if (list.some((item) => item.id === id)) {
   return list
  }
  return [{id, name, is_active: true}, ...list]
 }

 const toDropdownList = (list: unknown): IDropdown[] =>
  Array.isArray(list) ? (list as IDropdown[]) : []

 const loadInfraTypes = async (
  editInfra?: IInfra | null
 ): Promise<IInfraTypeOption[]> => {
  const fetched = await fetchInfraTypes()
  const types: IInfraTypeOption[] = Array.isArray(fetched)
   ? fetched
   : []
  if (editInfra?.infra_type) {
   return ensureInfraTypeOption(
    types,
    editInfra.infra_type,
    editInfra.infra_type
   )
  }
  return types
 }

 const loadStates = async (
  editInfra?: IInfra | null
 ): Promise<IDropdown[]> => {
  const states = toDropdownList(await fetchState())
  if (editInfra?.state) {
   return ensureDropdownOption(
    states,
    Number(editInfra.state),
    editInfra.state_name ?? String(editInfra.state)
   )
  }
  return states
 }

 const loadDistricts = async (
  stateId: number,
  editInfra?: IInfra | null
 ): Promise<IDropdown[]> => {
  const districts = toDropdownList(
   await fetchDistrict(stateId)
  )
  if (
   editInfra?.district &&
   Number(editInfra.state) === Number(stateId)
  ) {
   return ensureDropdownOption(
    districts,
    Number(editInfra.district),
    editInfra.district_name ?? String(editInfra.district)
   )
  }
  return districts
 }

 useEffect(() => {
  if (!props.show) return

  let cancelled = false

  const initForm = async () => {
   setUploadedFileData({})

   if (props.editInfra) {
    const [types, states, districts] = await Promise.all([
     loadInfraTypes(props.editInfra),
     loadStates(props.editInfra),
     loadDistricts(props.editInfra.state, props.editInfra),
    ])

    if (cancelled) return

    setInfraTypeList(types)
    setStateList(states)
    setDistrictList(districts)
    prevStateRef.current = String(props.editInfra.state)
    reset({
     infraType: props.editInfra.infra_type,
     state: props.editInfra.state,
     district: props.editInfra.district,
     pincode: props.editInfra.pincode,
     address: props.editInfra.address ?? '',
     gstNumber: props.editInfra.gst_no ?? '',
    })
    return
   }

   const [types, states] = await Promise.all([
    loadInfraTypes(),
    loadStates(),
   ])

   if (cancelled) return

   setInfraTypeList(types)
   setStateList(states)
   setDistrictList([])
   prevStateRef.current = ''
   reset({
    infraType: '',
    state: '',
    district: '',
    pincode: '',
    address: '',
    gstNumber: '',
   })
  }

  void initForm()

  return () => {
   cancelled = true
  }
 }, [props.show, props.editInfra, reset])

 useEffect(() => {
  if (!props.show || !selectedState) {
   if (!selectedState) {
    setDistrictList([])
   }
   return
  }

  const stateKey = String(selectedState)
  const prevStateKey = String(prevStateRef.current)
  const isStateChanged =
   prevStateRef.current !== '' && prevStateKey !== stateKey

  void (async () => {
   const districts = await loadDistricts(
    Number(selectedState),
    isStateChanged ? null : props.editInfra
   )
   setDistrictList(districts)
  })()

  if (isStateChanged) {
   setValue('district', '')
  }
  prevStateRef.current = stateKey
 }, [selectedState, setValue, props.show, props.editInfra])

 const handleClose = () => {
  props.onClose()
 }

 const onGstCertificateChange = (
  e: ChangeEvent<HTMLInputElement>
 ) => {
  void uploadFileChange(
   e,
   uploadedFileData,
   setUploading,
   setUploadedFileData,
   'gst_certificate'
  )
 }

 const uploadedGstFileName =
  uploadedFileData?.gst_certificate?.fileName
 const existingGstCertificateUrl =
  props.editInfra?.gst_certificate

 const submitForm = (data: {
  infraType: string
  state: string | number
  district: string | number
  pincode: string
  address: string
  gstNumber: string
 }) => {
  const gstCertificate =
   uploadedFileData?.gst_certificate?.filePath ??
   props.editInfra?.gst_certificate

  props.onSave({
   ...(props.editInfra?.id && {id: props.editInfra.id}),
   infra_type: data.infraType,
   state: Number(data.state),
   district: Number(data.district),
   pincode: data.pincode,
   address: data.address,
   is_active: props.editInfra?.is_active ?? true,
   ...(data.gstNumber && {gst_no: data.gstNumber}),
   ...(gstCertificate && {gst_certificate: gstCertificate}),
  })
 }

 return (
  <Model
   show={props.show}
   showHeader={false}
   closeBtnClass='absolute right-4 top-2 !bg-[#E6EBF0] !rounded-3xl w-7 h-7 !text-[#33393D]'
   header=''
   close={handleClose}
   modelClass='md:w-[800px]'
   modelContainerClass='!rounded-2xl'
   modelBodyClass='p-10'
  >
   <div className='w-full'>
    <p className='text-[#171B1F] text-left font-bold text-2xl'>
     {isEditMode
      ? t(
         'myAccountPage.businessProfile.offtaker.infra.editInfra'
        )
      : t(
         'myAccountPage.businessProfile.offtaker.infra.addInfra'
        )}
    </p>
    <form
     method='POST'
     action=''
     onSubmit={handleSubmit(submitForm)}
    >
     <div className='grid md:grid-cols-2 gap-5 w-full mt-7 text-[#171B1F]'>
      <Select2DropdownField
       label={t(
        'myAccountPage.businessProfile.offtaker.infra.infraType'
       )}
       labelClassName='padding-0 text-[#4E83E4] text-xs !font-medium'
       className={sharedClassName}
       wrapperClassName='relative w-full'
       inputWrapperClassName='pl-4 py-2 px-4'
       required={true}
       disabled={false}
       isClearable={false}
       isSearchable={true}
       control={control}
       customStyle={sharedSelectStyle}
       data={infraTypeList}
       rules={{
        validate: {
         required: (v: string) => {
          if (v === '' || v === undefined || v === null)
           return `${t('helperText.plsSelect')} ${t(
            'myAccountPage.businessProfile.offtaker.infra.infraType'
           )}`
         },
        },
       }}
       id='infraType'
       name='infraType'
       OptionValueKey='id'
       OptionLabelKey='name'
       placeholder={t(
        'myAccountPage.businessProfile.offtaker.infra.selectInfraType'
       )}
       errors={errors}
      />
      <Select2DropdownField
       label={t(
        'myAccountPage.businessProfile.offtaker.infra.state'
       )}
       labelClassName='padding-0 text-[#4E83E4] text-xs !font-medium'
       className={sharedClassName}
       wrapperClassName='relative w-full'
       inputWrapperClassName='pl-4 py-2 px-4'
       required={true}
       disabled={false}
       isClearable={false}
       isSearchable={true}
       control={control}
       customStyle={sharedSelectStyle}
       data={stateList}
       rules={{
        validate: {
         required: (v: string) => {
          if (v === '' || v === undefined || v === null)
           return `${t('helperText.plsSelect')} ${t(
            'myAccountPage.businessProfile.offtaker.infra.state'
           )}`
         },
        },
       }}
       id='state'
       name='state'
       OptionValueKey='id'
       OptionLabelKey='name'
       placeholder={t(
        'myAccountPage.businessProfile.offtaker.infra.selectState'
       )}
       errors={errors}
      />
      <Select2DropdownField
       label={t(
        'myAccountPage.businessProfile.offtaker.infra.district'
       )}
       labelClassName='padding-0 text-[#4E83E4] text-xs !font-medium'
       className={sharedClassName}
       wrapperClassName='relative w-full'
       inputWrapperClassName='pl-4 py-2 px-4'
       required={true}
       disabled={!selectedState}
       isClearable={false}
       isSearchable={true}
       control={control}
       customStyle={sharedSelectStyle}
       data={districtList}
       rules={{
        validate: {
         required: (v: string) => {
          if (v === '' || v === undefined || v === null)
           return `${t('helperText.plsSelect')} ${t(
            'myAccountPage.businessProfile.offtaker.infra.district'
           )}`
         },
        },
       }}
       id='district'
       name='district'
       OptionValueKey='id'
       OptionLabelKey='name'
       placeholder={t(
        'myAccountPage.businessProfile.offtaker.infra.selectDistrict'
       )}
       errors={errors}
      />
      <TextField
       label={t(
        'myAccountPage.businessProfile.offtaker.infra.pincode'
       )}
       labelClassName='padding-0 text-[#4E83E4] text-xs !font-medium'
       className='!text-[#33393D]'
       wrapperClassName='relative w-full'
       inputWrapperClassName='pl-4 py-2 px-4'
       type='text'
       required={true}
       id='pincode'
       name='pincode'
       placeholder={t(
        'myAccountPage.businessProfile.offtaker.infra.enterPincode'
       )}
       register={register('pincode', {
        required: `${t('helperText.plsEnter')} ${t(
         'myAccountPage.businessProfile.offtaker.infra.pincode'
        )}`,
       })}
       errors={errors}
      />
      <TextField
       label={t(
        'myAccountPage.businessProfile.offtaker.infra.address'
       )}
       labelClassName='padding-0 text-[#4E83E4] text-xs !font-medium'
       className='!text-[#33393D]'
       wrapperClassName='relative w-full'
       inputWrapperClassName='pl-4 py-2 px-4'
       type='text'
       required={true}
       id='address'
       name='address'
       placeholder={t(
        'myAccountPage.businessProfile.offtaker.infra.enterAddress'
       )}
       register={register('address', {
        required: `${t('helperText.plsEnter')} ${t(
         'myAccountPage.businessProfile.offtaker.infra.address'
        )}`,
       })}
       errors={errors}
      />
      <TextField
       label={t(
        'myAccountPage.businessProfile.offtaker.infra.gstNumber'
       )}
       labelClassName='padding-0 text-[#4E83E4] text-xs !font-medium'
       className='!text-[#33393D]'
       wrapperClassName='relative w-full'
       inputWrapperClassName='pl-4 py-2 px-4'
       type='text'
       required={true}
       id='gstNumber'
       name='gstNumber'
       placeholder={t(
        'myAccountPage.businessProfile.offtaker.infra.enterGstNumber'
       )}
       register={register('gstNumber', {
        required: `${t('helperText.plsEnter')} ${t(
         'myAccountPage.businessProfile.offtaker.infra.gstNumber'
        )}`,
       })}
       errors={errors}
      />
     </div>

     <div className='w-full mt-6'>
      <p className='text-[#171B1F] text-base font-bold mb-3'>
       {t(
        'myAccountPage.businessProfile.offtaker.infra.uploadGstCertificate'
       )}
      </p>
      <label
       htmlFor={fileInputId}
       className='flex items-center gap-3 w-full min-h-[88px] cursor-pointer rounded-lg border border-dashed border-[#D7DFEA] bg-[#F5F8FA] px-5 py-4'
      >
       <KTSVG
        path='/media/icons/uploadFile.svg'
        svgClassName='w-5 h-5 shrink-0'
       />
       <div className='flex flex-col'>
        <span className='text-[#171B1F] text-sm font-bold'>
         {uploading ? (
          t('bulkUpload.uploading')
         ) : uploadedGstFileName ? (
          uploadedGstFileName
         ) : existingGstCertificateUrl ? (
          <a
           href={existingGstCertificateUrl}
           target='_blank'
           rel='noreferrer'
           className='text-[#4E83E4]'
           onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            window.open(
             existingGstCertificateUrl,
             '_blank',
             'noopener,noreferrer'
            )
           }}
          >
           {t(
            'myAccountPage.businessProfile.offtaker.infra.uploadedDocument'
           )}
          </a>
         ) : (
          t(
           'myAccountPage.businessProfile.offtaker.paymentTerms.clickToUpload'
          )
         )}
        </span>
        <span className='text-[#80868C] text-xs mt-0.5'>
         {t(
          'myAccountPage.businessProfile.offtaker.paymentTerms.supportedFormats'
         )}
        </span>
       </div>
      </label>
      <input
       id={fileInputId}
       name='gst_certificate'
       type='file'
       className='hidden'
       accept='.pdf,.jpg,.jpeg,.png'
       disabled={uploading}
       onChange={onGstCertificateChange}
      />
     </div>

     <div className='flex w-full gap-4 mt-8'>
      <Button
       type='button'
       onClick={handleClose}
       color='blue'
       className='flex-1 h-12 text-center bg-white !rounded-lg text-base !text-[#4E83E4] font-semibold border border-[#4E83E4]'
      >
       {t(
        'myAccountPage.businessProfile.offtaker.infra.cancel'
       )}
      </Button>
      <Button
       type='submit'
       color='blue'
       disabled={!isValid || uploading}
       className='flex-1 h-12 text-center bg-[#4E83E4] !rounded-lg text-base text-white font-semibold border border-[#4E83E4]'
      >
       {t(
        'myAccountPage.businessProfile.offtaker.infra.save'
       )}
      </Button>
     </div>
    </form>
   </div>
  </Model>
 )
}

export default AddInfraModal
